import { ReplyIcon } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import classNames from "components/ui/classNames";

import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { useForm } from "react-hook-form";
import type { ISelfieQuery } from "../../../lib/queries/__generated__/SelfieQuery.generated";
import Avatar from "../../core/Avatar";
import CommentInputTextArea from "../CommentInputTextArea";

import type { CommentInputFormData } from "./shapes/commentInputFormDataShape";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IReplyCommentInputFormProps {
  viewer: NonNullable<ISelfieQuery["selfie"]>;
  bodyAsPlainText?: string;
  hideAvatar?: boolean;
  autoFocus?: boolean;
  hintAboutSupportMessagesBeingDeleted?: boolean;
  onCancel?: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function ReplyCommentInputForm({
  viewer,
  bodyAsPlainText = "",
  hideAvatar = false,
  autoFocus = false,
  onSubmit,
}: IReplyCommentInputFormProps) {
  const intl = useIntl();
  const form = useForm<CommentInputFormData>({
    mode: "onSubmit",
    resolver: zodResolver(commentInputFormDataShape),
    defaultValues: {
      bodyAsPlainText,
    },
  });

  const {
    formState: { isSubmitting },
  } = form;

  const handleSubmit = async ({ bodyAsPlainText }: CommentInputFormData) => {
    try {
      await onSubmit(bodyAsPlainText);

      setTimeout(() => {
        form.reset();
      }, 1);
    } catch (err) {
      form.setError("root.serverError", {
        message: translateServerErrorIntoFriendlyMessage(
          err,
          intl,
          intl.formatMessage({
            defaultMessage: "Não foi possível enviar sua mensagem agora. Tente novamente mais tarde.",
            id: "AsTfN9",
          }),
        ),
      });
    }
  };

  return (
    <div className="space-y-2">
      <div
        className={classNames("grid grid-cols-1 gap-x-4 sm:grid-cols-[42px_1fr]", {
          "grid-cols-1 sm:grid-cols-1": hideAvatar,
        })}
      >
        {!hideAvatar && (
          <Avatar
            src={viewer.pictureUrl}
            hashValue={viewer._id}
            alt={viewer.fullName}
            size={42}
            className="hidden sm:inline-flex"
          />
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-wrap items-start gap-x-2 gap-y-4 sm:flex-nowrap"
          >
            <FormField
              control={form.control}
              name="bodyAsPlainText"
              render={({ field }) => {
                return (
                  <div className="w-full space-y-2">
                    <CommentInputTextArea
                      {...field}
                      autoFocus={autoFocus}
                      placeholder={intl.formatMessage({
                        id: "SQv765",
                        defaultMessage: "Escreva uma resposta aqui...",
                      })}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                          form.handleSubmit(handleSubmit)();
                        }
                      }}
                    />

                    {field.value && <FormMessage />}
                    <FormMessage asRootError={true} />
                  </div>
                );
              }}
            />

            <button
              type="submit"
              disabled={isSubmitting}
              className={classNames(
                "w-full rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600 sm:w-auto",
                "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "dark:bg-teal-600/20 dark:text-teal-200",
                "disabled:opacity-60",
                "dark:disabled:bg-slate-600/20 dark:disabled:text-slate-500",
              )}
            >
              <ReplyIcon strokeWidth={2} className="hidden sm:inline-flex" />
              <span className="inline-flex w-full justify-center text-sm font-medium sm:hidden">
                <FormattedMessage defaultMessage="Enviar resposta" id="f6/BJx" />
              </span>
            </button>
          </form>
        </Form>
      </div>
    </div>
  );
}
